import dayjs from 'dayjs';
import { TimeZone } from '../../../time/timezone.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, SerializationContext, SerializedData, DataModelBuilder } from '../../dto/data-model.js';
import { convertFromLanguageMap, convertToLanguageMap, isSameMap } from '../../dto/data-model-utils.js';
import { Language } from '../language.js';

export class NewsPost extends DataModel {
  static readonly TYPE_ID: string = '020ac8eac6f94ecaa7e8d974f53d6a06';

  constructor(
    public readonly uniqueId: string,
    public readonly url: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly title: Map<Language, string>,
    public readonly description: Map<Language, string>,
    public readonly coverPhoto: Map<Language, string>,
  ) {
    super();
  }

  static fromData(args: {
    uniqueId: string,
    url: string | null,
    creationDate: dayjs.Dayjs,
    title: Map<Language, string>,
    description: Map<Language, string>,
    coverPhoto: Map<Language, string>,
  }): NewsPost {
    return new NewsPost( args.uniqueId, args.url, args.creationDate, args.title, args.description, args.coverPhoto);
  }

  get $typeId(): string {
    return NewsPost.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): NewsPost {
    return new NewsPost(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'url'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        convertToLanguageMap(map, 'title'),
        convertToLanguageMap(map, 'description'),
        convertToLanguageMap(map, 'coverPhoto'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'url': this.url,
      'creationDate': context.encodeDate(this.creationDate),
      'title': convertFromLanguageMap(this.title),
      'description': convertFromLanguageMap(this.description),
      'coverPhoto': convertFromLanguageMap(this.coverPhoto),
    };
  }

  clone(): this {
    return new NewsPost(
        this.uniqueId,
        this.url,
        TimeZone.cloneDate(this.creationDate),
        this.title,
        this.description,
        this.coverPhoto,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.url === this.url &&
        other.creationDate === this.creationDate &&
        isSameMap(other.title, this.title) &&
        isSameMap(other.description, this.description) &&
        isSameMap(other.coverPhoto, this.coverPhoto);
  }
}

export class NewsPostBuilder extends DataModelBuilder<NewsPost> {
  build(context: SerializationContext, map: SerializedData): NewsPost {
    return NewsPost.fromMap(context, map);
  }
}
